import { Auth } from '@util/Auth';
import {
  MedicareAccessBHSymptomDiary,
  MedicareAccessTrack,
  User,
  withDocCreationTimestamps,
  withDocUpdateTimestamp,
} from 'hydra-data-types';
import { isNonEmpty } from 'ts-utils';
import {
  getUserDocRef,
  medicareAccessSymptomDiaryDocRef,
  userDataDocRef,
} from '../../../helpers/CommonDatabaseOperations';
import {
  getMedicareAccessStage,
  splitOutMedicareAccessInitialSymptoms,
} from '../../../util/MedicareAccessSymptomUtils';
import { store as baseStore } from '../../Store';
import { prepareUserDataForPersistence } from '../PrepareUserDataForPersistence';

/** Store a users userData to the database if they're authenticated. */
export async function updateUserDataInDb(store = baseStore): Promise<void> {
  const { user, userData: initialUserData } = store.getState();

  if (Auth.isUserSignedIn() && user.userId !== undefined) {
    const { userData, medicareAccessBHSymptom, medicareAccessUser } =
      splitOutMedicareAccessInitialSymptoms(prepareUserDataForPersistence(initialUserData));
    const updates: Array<Promise<void>> = [];

    if (isNonEmpty(userData))
      updates.push(userDataDocRef(user.userId).set(userData, { merge: true }));

    if (isNonEmpty(medicareAccessBHSymptom)) {
      updates.push(updateMedicareAccessInitialBHSymptomsInDb(user.userId, medicareAccessBHSymptom));
    }

    if (isNonEmpty(medicareAccessUser)) {
      updates.push(getUserDocRef(user.userId).set(medicareAccessUser, { merge: true }));
    }

    await Promise.all(updates);
  }
}

/** Store a users medicare access **behavioural health (bh)** symptoms to the database. */
export async function updateMedicareAccessInitialBHSymptomsInDb(
  userId: User['userId'],
  symptoms: Partial<MedicareAccessBHSymptomDiary>
): Promise<void> {
  const track = MedicareAccessTrack.BehaviouralHealth;
  const docRef = medicareAccessSymptomDiaryDocRef({ userId, track });
  const diary = { ...symptoms, stage: getMedicareAccessStage(), track };

  // createdAt is set once; updatedAt on every write (the diary is merged as each PROM is answered).
  const existing = await docRef.get();
  await docRef.set(
    existing.exists ? withDocUpdateTimestamp(diary) : withDocCreationTimestamps(diary),
    { merge: true }
  );
}
